import { IconName, PropertyColor } from '@thiememeulenhoff/types';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { IconSize } from '@thiememeulenhoff/tokens';
import { ButtonComponent } from '../button/button.component';
import { styles, tag } from './icon-button.styles';

@customElement(tag)
export class IconButtonComponent extends ButtonComponent {
  static override styles = styles;

  @property({ type: Boolean })
  underlined = false;

  @property({ type: Boolean })
  indicator = false;

  @property({ type: String })
  icon: IconName = 'bookmark';

  @property({ type: String, reflect: true })
  override color: PropertyColor = 'blue';

  @property({ type: String, attribute: 'icon-size' })
  iconSize: IconSize = 'large';

  @property({ type: String })
  label = '';

  @property({ type: Boolean })
  private hovering = false;

  currentIcon(): IconName {
    if (this.variant !== 'dual') {
      return this.icon;
    }

    const hasSolid = this.icon.endsWith('-solid');

    if (this.hovering) {
      return hasSolid
        ? (this.icon.replace('-solid', '') as IconName)
        : (`${this.icon}-solid` as IconName);
    }

    return this.icon;
  }

  override render() {
    const content = html`
      <a-icon
        part="icon"
        name="${this.currentIcon()}"
        class="${this.iconSize}"
      ></a-icon>
      ${this.renderLabel()} ${this.renderIndicator()}
    `;

    const ariaLabel = this.#ariaLabel();

    if (this.href) {
      return html`<a
        part="button"
        class="button ${!this.label ? 'without-content' : ''}"
        href=${this.disabled ? nothing : this.href}
        target=${this.target ?? nothing}
        ?disabled=${this.disabled}
        ?underlined=${this.underlined}
        aria-label="${ariaLabel ?? nothing}"
        @mouseenter=${this.#onMouseEnter}
        @mouseleave=${this.#onMouseLeave}
      >
        ${content}
      </a>`;
    }

    return html`<button
      part="button"
      class="button ${!this.label ? 'without-content' : ''}"
      aria-label="${ariaLabel ?? nothing}"
      ?disabled=${this.disabled}
      ?underlined=${this.underlined}
      @click=${this.handleClick}
      @mouseenter=${this.#onMouseEnter}
      @mouseleave=${this.#onMouseLeave}
    >
      ${content}
    </button>`;
  }

  #ariaLabel() {
    // If the host has an aria-label, we only add an aria-label if we have a label
    if (this.ariaLabel) {
      return this.label || null;
    }

    return this.label || this.icon;
  }

  #onMouseEnter() {
    this.hovering = true;
  }

  #onMouseLeave() {
    this.hovering = false;
  }

  renderLabel() {
    return this.label.length > 0 ? html`<label>${this.label}</label>` : nothing;
  }

  renderIndicator() {
    return this.indicator ? html` <div class="indicator"></div>` : nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-icon-button': IconButtonComponent;
  }
}
